import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, ArrowRight, ExternalLink } from "lucide-react";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import api from "../lib/api";

const PHONE = "+91 9900186556";
const PHONE_DIGITS = "919900186556";
const EMAIL = "Buildbrigadeenterprises@gmail.com";
const ADDRESS = "Hoskote, Bengaluru Rural, Karnataka";
const MAP_QUERY = "Hoskote, Bengaluru Rural, Karnataka";
const MAP_EMBED_SRC = `https://www.google.com/maps?q=${encodeURIComponent(MAP_QUERY)}&output=embed`;
const MAP_DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(MAP_QUERY)}`;

const PURPOSES = [
  "Buy Property", "Sell Property", "Turnkey Construction",
  "NRI Management", "Commercial Leasing", "Distressed / JV",
  "Interior Design", "General Enquiry",
];

export default function Contact() {
  const [f, setF] = useState({ name: "", email: "", phone: "", purpose: PURPOSES[0], requirement: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const openWhatsApp = () => {
    const lines = [
      "*New Enquiry — Build Brigade Enterprises*",
      "",
      `*Name:* ${f.name}`,
      `*Email:* ${f.email}`,
      `*Phone:* ${f.phone}`,
      `*Purpose:* ${f.purpose}`,
      "",
      "*Requirement:*",
      f.requirement || "—",
    ];
    const msg = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${PHONE_DIGITS}?text=${msg}`, "_blank", "noopener");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) return toast.error("Enter a valid email");
    if (!/^[+0-9 \-()]{7,20}$/.test(f.phone)) return toast.error("Enter a valid phone");
    if (!f.name.trim()) return toast.error("Enter your name");
    setLoading(true);
    try {
      await api.post("/contact", {
        name: f.name, email: f.email, phone: f.phone,
        subject: f.purpose, message: f.requirement,
      });
      openWhatsApp();
      toast.success("Opening WhatsApp with your enquiry pre-filled. Tap Send to reach our team.");
      setF({ name: "", email: "", phone: "", purpose: PURPOSES[0], requirement: "" });
    } catch {
      // Still allow WhatsApp handoff even if backend save fails
      openWhatsApp();
      toast.message("Opening WhatsApp — we'll follow up once you send the message.");
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-[112px]">
      <section className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="eyebrow mb-6 flex items-center gap-3"><span className="accent-dot" /> Contact · Bengaluru Desk</div>
        <h1 className="display-1 max-w-6xl" data-testid="contact-title">
          <MaskedLines lines={["Start a conversation.", "Skip the brochure."]} />
        </h1>
        <p className="mt-10 max-w-2xl text-[15px] leading-[1.8] text-[#EDE6D3]/75">
          Walk into our Hoskote office, ping us on WhatsApp, or fill out the enquiry below and we'll route it straight to a partner.
        </p>
      </section>

      {/* Direct channels */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-24 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Reveal>
          <a href={`tel:${PHONE.replace(/\s+/g, "")}`} className="card-sand p-8 block group h-full" data-testid="contact-phone">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="eyebrow">Direct Line</div>
                <div className="mt-4 font-serif text-[26px] md:text-[30px] leading-none tracking-tight group-hover:text-[#D4AF37] transition-colors">{PHONE}</div>
                <div className="mt-4 text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/50">Mon – Sat · 09:30 – 20:00 IST</div>
              </div>
              <div className="border border-[#D4AF37] p-3 shrink-0"><Phone size={18} color="#D4AF37" /></div>
            </div>
          </a>
        </Reveal>
        <Reveal delay={0.08}>
          <a href={`https://wa.me/${PHONE_DIGITS}`} target="_blank" rel="noreferrer" className="card-sand p-8 block group h-full" data-testid="contact-whatsapp">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="eyebrow">WhatsApp</div>
                <div className="mt-4 font-serif text-[26px] md:text-[30px] leading-tight tracking-tight group-hover:text-[#D4AF37] transition-colors">Chat with our desk</div>
                <div className="mt-4 text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/50">Replies within 4 hours</div>
              </div>
              <div className="border border-[#D4AF37] p-3 shrink-0"><MessageCircle size={18} color="#D4AF37" /></div>
            </div>
          </a>
        </Reveal>
        <Reveal delay={0.16}>
          <a href={`mailto:${EMAIL}`} className="card-sand p-8 block group h-full break-all" data-testid="contact-email">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="eyebrow">Correspondence</div>
                <div className="mt-4 font-serif text-[18px] md:text-[22px] leading-tight tracking-tight group-hover:text-[#D4AF37] transition-colors">{EMAIL}</div>
                <div className="mt-4 text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/50">Reviewed within 24 hours</div>
              </div>
              <div className="border border-[#D4AF37] p-3 shrink-0"><Mail size={18} color="#D4AF37" /></div>
            </div>
          </a>
        </Reveal>
      </section>

      {/* Office + Map */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-24" data-testid="office-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <Reveal className="md:col-span-5">
            <div className="chapter-num">§ Office</div>
            <h2 className="mt-4 font-serif text-[38px] md:text-[52px] leading-[1.05] tracking-tight">Hoskote HQ.</h2>
            <div className="mt-8 flex items-start gap-3 text-[14px] leading-[1.8] text-[#EDE6D3]/85">
              <MapPin size={18} color="#D4AF37" className="mt-[3px] shrink-0" />
              <span data-testid="office-address">{ADDRESS}</span>
            </div>
            <div className="mt-6 flex items-center gap-3 text-[13px] text-[#EDE6D3]/75">
              <Clock size={14} color="#D4AF37" /> Mon – Sat · 09:30 – 20:00 IST
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href={MAP_DIRECTIONS} target="_blank" rel="noreferrer" className="btn-primary" data-testid="get-directions">
                Get Directions <ExternalLink size={14} />
              </a>
              <a href={`https://wa.me/${PHONE_DIGITS}?text=${encodeURIComponent("Hi, I'd like to book an in-person meeting at your Hoskote office.")}`}
                 target="_blank" rel="noreferrer" className="btn-outline" data-testid="book-visit">
                Book Office Visit <MessageCircle size={14} />
              </a>
            </div>
          </Reveal>
          <Reveal className="md:col-span-7" delay={0.1}>
            <div className="border border-[#EDE6D3]/15 overflow-hidden aspect-[16/11]" data-testid="office-map">
              <iframe
                title="Build Brigade Enterprises — Hoskote office"
                src={MAP_EMBED_SRC}
                width="100%" height="100%"
                style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.8) contrast(0.95)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div className="mt-3 text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/50">Landmark · Hoskote, Bengaluru Rural</div>
          </Reveal>
        </div>
      </section>

      {/* WhatsApp Enquiry form */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32" data-testid="enquiry-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="chapter-num">§ Send Enquiry</div>
            <h2 className="mt-4 font-serif text-[38px] md:text-[52px] leading-[1.05] tracking-tight">Straight to WhatsApp.</h2>
            <p className="mt-6 text-[14px] leading-[1.8] text-[#EDE6D3]/70 max-w-md">
              Fill this in and tap send. Your enquiry lands directly on our partner's WhatsApp with your name, contact, purpose and requirement — no back-and-forth.
            </p>
            <ul className="mt-10 space-y-3 text-[13px] text-[#EDE6D3]/80">
              <li>— Delivered to <span className="text-[#D4AF37]">{PHONE}</span></li>
              <li>— Message auto-formatted for our team</li>
              <li>— We also log a copy for the acquisitions desk</li>
            </ul>
          </div>
          <form onSubmit={submit} className="md:col-span-7 card-sand p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6" data-testid="whatsapp-form">
            <div className="md:col-span-2 flex items-center justify-between mb-2">
              <div className="eyebrow">WhatsApp Enquiry</div>
              <div className="chapter-num">// To {PHONE}</div>
            </div>
            <div><div className="bb-label">Full Name</div><input required className="bb-input" value={f.name} onChange={set("name")} data-testid="wa-name" /></div>
            <div><div className="bb-label">Email</div><input required type="email" className="bb-input" value={f.email} onChange={set("email")} data-testid="wa-email" /></div>
            <div><div className="bb-label">Phone</div><input required className="bb-input" value={f.phone} onChange={set("phone")} placeholder="+91 98xxxxxxxx" data-testid="wa-phone" /></div>
            <div><div className="bb-label">Purpose</div>
              <select className="bb-input" value={f.purpose} onChange={set("purpose")} data-testid="wa-purpose">
                {PURPOSES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="md:col-span-2"><div className="bb-label">Requirement</div>
              <textarea rows={5} className="bb-input" value={f.requirement} onChange={set("requirement")} data-testid="wa-requirement"
                placeholder="Tell us what you're looking for — location, budget, timeline, plot size or square-footage." />
            </div>
            <button type="submit" disabled={loading} className="btn-primary md:col-span-2 justify-center disabled:opacity-50" data-testid="wa-submit">
              {loading ? "Preparing…" : "Send via WhatsApp"} <ArrowRight size={14} />
            </button>
            <p className="md:col-span-2 mt-1 text-[11px] tracking-[1px] uppercase text-[#EDE6D3]/50 text-center">Opens WhatsApp with your details pre-filled · tap Send inside WhatsApp to complete.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
